import { createContext, ReactNode, useCallback, useContext, useLayoutEffect, useMemo, useState } from "react"

type FeedbackContextValue = {
  open: boolean
  setOpen: (open: boolean) => void
  /** True while a page's navigation is showing its own "Feedback" item. */
  hasNavTrigger: boolean
  registerNavTrigger: () => () => void
}

const FeedbackContext = createContext<FeedbackContextValue | null>(null)

/**
 * Shared state for the feedback dialog. It wraps the whole Inertia app so the
 * dialog (mounted once) can be opened from the sidebar nav on app pages, or
 * from a floating button on pages without a nav (login, sign up, home).
 */
export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const [navTriggers, setNavTriggers] = useState(0)

  const registerNavTrigger = useCallback(() => {
    setNavTriggers((n) => n + 1)
    return () => setNavTriggers((n) => n - 1)
  }, [])

  const value = useMemo(
    () => ({ open, setOpen, hasNavTrigger: navTriggers > 0, registerNavTrigger }),
    [open, navTriggers, registerNavTrigger],
  )

  return <FeedbackContext.Provider value={value}>{children}</FeedbackContext.Provider>
}

export function useFeedback() {
  const context = useContext(FeedbackContext)
  if (!context) throw new Error("useFeedback must be used inside <FeedbackProvider>")
  return context
}

/** Call from a nav that renders its own feedback item, so the floating button steps aside. */
export function useRegisterNavTrigger() {
  const { registerNavTrigger } = useFeedback()
  useLayoutEffect(() => registerNavTrigger(), [registerNavTrigger])
}
